import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Chip,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { ROLE_LABELS, formatDisplayName, type Role, type User, type Department } from '@altyor-kronos/shared-types';
import { useAuth } from '../../core/auth/useAuth';
import { useCollectionSnapshot, orderByField } from '../../core/data/useCollectionSnapshot';
import { setUserDepartment, setUserJobTitle, setUserName, setUserActive } from '../../core/data/adminMutations';
import UserInvitesPanel from './UserInvitesPanel';
import { adminDataGridSx } from './adminDataGridSx';
import { foldText } from '../kronos/entryFilter';
import { colors } from '../../core/theme/theme';

function JobTitleCell({
  id, initial, disabled, onSave,
}: {
  id: string; initial: string; disabled: boolean;
  onSave: (id: string, value: string) => void;
}) {
  const [value, setValue] = useState(initial);
  // Keep in sync if the snapshot pushes a new value from elsewhere.
  useEffect(() => setValue(initial), [initial]);
  const commit = () => {
    if (value.trim() !== initial.trim()) onSave(id, value.trim());
  };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
      <TextField
        variant="standard"
        size="small"
        fullWidth
        value={value}
        disabled={disabled}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setValue(e.target.value)}
        onBlur={(e) => {
          e.stopPropagation();
          commit();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            (e.target as HTMLInputElement).blur();
          }
          e.stopPropagation();
        }}
        data-testid={`jobtitle-input-${id}`}
        sx={{
          // Match the 14px of plain-text cells so the row baseline stays aligned.
          '& .MuiInputBase-input': { fontSize: 14, py: 0.25 },
          // Underline only as a subtle affordance — keep it light until focus.
          '& .MuiInput-underline:before': { borderBottomColor: 'transparent' },
        }}
      />
    </Box>
  );
}

/** Inline editable name cell (mirrors JobTitleCell; commits via the passed onSave). */
function NameCell({
  id, initial, disabled, onSave,
}: {
  id: string; initial: string; disabled: boolean;
  onSave: (id: string, value: string) => void;
}) {
  const [value, setValue] = useState(initial);
  useEffect(() => setValue(initial), [initial]);
  const commit = () => {
    if (value.trim() !== initial.trim()) onSave(id, value.trim());
  };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
      <TextField
        variant="standard"
        size="small"
        fullWidth
        value={value}
        disabled={disabled}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setValue(e.target.value)}
        onBlur={(e) => {
          e.stopPropagation();
          commit();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            (e.target as HTMLInputElement).blur();
          }
          e.stopPropagation();
        }}
        data-testid={`name-input-${id}`}
        sx={{
          '& .MuiInputBase-input': { fontSize: 14, py: 0.25 },
          '& .MuiInput-underline:before': { borderBottomColor: 'transparent' },
        }}
      />
    </Box>
  );
}

/**
 * Admin Rôles tab — the tenant's users, with their roles shown but NOT editable.
 *
 * Roles are not editable here because editing them from the app was a lie. AuthZ
 * is token-driven (`kronos-api/src/authz/predicates.ts` reads `identity.roles`,
 * which comes from the Entra access token); the `users.roles` array is only a
 * display mirror. Writing the mirror changed the chips and nothing else — and
 * making the write real would have meant granting kronos-api the standing Graph
 * permission `AppRoleAssignment.ReadWrite.All` over the whole tenant, which was
 * refused on 2026-09-18 for the same reason `User.Read.All` was.
 *
 * So: roles are assigned in the Entra portal, and `tools/users/reconcile-roles.ts`
 * brings this mirror back in line. Everything else on this tab (name, job title,
 * group, active) is real Kronos data and stays editable.
 */
export default function UsersTab() {
  const { t, i18n } = useTranslation();
  const lang: 'en' | 'fr' = i18n.language?.startsWith('fr') ? 'fr' : 'en';
  const { rows, loading, error } = useCollectionSnapshot<User>(
    'users',
    orderByField('displayName'),
  );

  const { user } = useAuth();
  const isAdmin = user?.roles.includes('admin') ?? false;
  const { rows: departmentRows } = useCollectionSnapshot<Department>('departments');
  const departmentsById = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of departmentRows) m.set(d.id, d.data.name);
    return m;
  }, [departmentRows]);
  const departmentOptions = useMemo(
    () =>
      departmentRows
        .map((d) => ({ id: d.id, name: d.data.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [departmentRows],
  );
  const [groupFilter, setGroupFilter] = useState('all');

  const [search, setSearch] = useState('');
  // Optimistic overlay: the poller now re-reads as soon as a write lands, but
  // that still costs a round-trip (300-600ms from China). Merge just-written
  // fields into the rows so an inline edit renders at 0ms, then let the refetch
  // reconcile.
  const [pending, setPending] = useState<Record<string, Partial<User>>>({});
  const [writeError, setWriteError] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = foldText(search.trim());
    return rows.filter(({ data }) => {
      const matchesText =
        !q ||
        foldText(data.displayName ?? '').includes(q) ||
        foldText(data.email ?? '').includes(q);
      const matchesGroup = groupFilter === 'all' || (data.departmentId ?? '') === groupFilter;
      return matchesText && matchesGroup;
    });
  }, [rows, search, groupFilter]);

  const gridRows = useMemo(
    () =>
      filtered.map(({ id, data }) => {
        // Overlay any optimistic edits on top of the last-polled server data.
        const d = { ...data, ...pending[id] };
        return {
          id,
          // Spread the User shape so the action column has access to it for the dialog.
          user: d,
          displayName: d.displayName ?? '',
          firstName: d.firstName ?? '',
          lastName: d.lastName ?? '',
          active: d.active !== false,
          email: d.email ?? '',
          jobTitle: d.jobTitle ?? '',
          departmentId: d.departmentId ?? '',
          roles: d.roles ?? [],
          photoURL: d.photoURL ?? '',
        };
      }),
    [filtered, pending],
  );

  // Current best-known value for a user (server data + optimistic overlay).
  const currentData = (id: string): Partial<User> => ({
    ...filtered.find((r) => r.id === id)?.data,
    ...pending[id],
  });
  const applyPending = (id: string, patch: Partial<User>) =>
    setPending((p) => ({ ...p, [id]: { ...p[id], ...patch } }));

  /**
   * Apply the optimistic overlay, then confirm with the server. These five
   * writes used to be fire-and-forget: a rejected write left the optimistic
   * value on screen for the rest of the session and the admin was never told
   * the change hadn't saved. Revert the overlay and surface the error instead.
   */
  const writeField = async (
    id: string,
    patch: Partial<User>,
    send: () => Promise<void>,
  ): Promise<void> => {
    const current = currentData(id);
    const before = Object.fromEntries(
      Object.keys(patch).map((k) => [k, current[k as keyof User]]),
    ) as Partial<User>;
    applyPending(id, patch);
    try {
      await send();
    } catch (e) {
      applyPending(id, before);
      setWriteError(e instanceof Error ? e.message : String(e));
    }
  };

  const writeGroup = (id: string, departmentId: string) =>
    void writeField(id, { departmentId }, () => setUserDepartment(id, departmentId));
  const writeJobTitle = (id: string, jobTitle: string) =>
    void writeField(id, { jobTitle }, () => setUserJobTitle(id, jobTitle));
  const writeFirst = (id: string, v: string) => {
    const lastName = currentData(id).lastName ?? '';
    return void writeField(
      id,
      { firstName: v, displayName: formatDisplayName(v, lastName) },
      () => setUserName(id, v, lastName),
    );
  };
  const writeLast = (id: string, v: string) => {
    const firstName = currentData(id).firstName ?? '';
    return void writeField(
      id,
      { lastName: v, displayName: formatDisplayName(firstName, v) },
      () => setUserName(id, firstName, v),
    );
  };
  const writeActive = (id: string, active: boolean) =>
    void writeField(id, { active }, () => setUserActive(id, active));

  function getInitials(name: string, email: string): string {
    const source = name.trim() || email.trim();
    if (!source) return '?';
    return source.split(/\s+|[._-]/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('') || '?';
  }

  const columns: GridColDef[] = [
    {
      field: 'avatar',
      headerName: '',
      width: 52,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Avatar
            src={params.row.photoURL || undefined}
            variant="square"
            sx={{
              width: 40,
              height: 40,
              fontSize: 14,
              fontWeight: 700,
              bgcolor: colors.lightGray,
              color: colors.bodyText,
            }}
          >
            {!params.row.photoURL && getInitials(params.row.displayName, params.row.email)}
          </Avatar>
        </Box>
      ),
    },
    {
      field: 'lastName',
      headerName: t('admin.roles.columns.lastName'),
      flex: 1.1,
      minWidth: 120,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%', width: '100%' }}>
          <NameCell id={params.row.id} initial={params.row.lastName} disabled={!isAdmin} onSave={writeLast} />
          {!params.row.active && (
            <Chip
              label={t('admin.roles.formerBadge')}
              size="small"
              sx={{ borderRadius: 0, fontSize: 11, height: 20, backgroundColor: colors.lightGray, color: colors.bodyText }}
            />
          )}
        </Box>
      ),
    },
    {
      field: 'firstName',
      headerName: t('admin.roles.columns.firstName'),
      flex: 1.1,
      minWidth: 120,
      sortable: true,
      renderCell: (params) => (
        <NameCell id={params.row.id} initial={params.row.firstName} disabled={!isAdmin} onSave={writeFirst} />
      ),
    },
    {
      field: 'email',
      headerName: t('admin.roles.columns.email'),
      flex: 1.3,
      minWidth: 150,
    },
    {
      field: 'jobTitle',
      headerName: t('admin.roles.columns.jobTitle'),
      flex: 0.9,
      minWidth: 110,
      sortable: true,
      renderCell: (params) => (
        <JobTitleCell
          id={params.row.id}
          initial={params.row.jobTitle}
          disabled={!isAdmin}
          onSave={writeJobTitle}
        />
      ),
    },
    {
      field: 'group',
      headerName: t('admin.roles.columns.group'),
      flex: 1,
      minWidth: 130,
      sortable: true,
      // Sort by resolved group name so the column sorts alphabetically.
      valueGetter: (_value: unknown, row: { departmentId: string }) => departmentsById.get(row.departmentId) ?? '',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
          <Select
            size="small"
            variant="standard"
            fullWidth
            value={departmentsById.has(params.row.departmentId) ? params.row.departmentId : ''}
            disabled={!isAdmin}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              e.stopPropagation();
              writeGroup(params.row.id, e.target.value as string);
            }}
            displayEmpty
            data-testid={`group-select-${params.row.id}`}
            sx={{
              // Match the 14px plain-text cells; keep the underline subtle until focus.
              fontSize: 14,
              '& .MuiSelect-select': { py: 0.25 },
              '&.MuiInput-underline:before': { borderBottomColor: 'transparent' },
            }}
          >
            <MenuItem value="">
              <em>{t('admin.roles.noGroup')}</em>
            </MenuItem>
            {departmentOptions.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {d.name}
              </MenuItem>
            ))}
          </Select>
        </Box>
      ),
    },
    {
      field: 'roles',
      headerName: t('admin.roles.columns.roles'),
      flex: 1.3,
      minWidth: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 0.5,
            alignItems: 'center',
            height: '100%',
            py: 0.5,
          }}
        >
          {(params.row.roles as Role[]).map((role) => (
            <Chip
              key={role}
              label={ROLE_LABELS[role]?.[lang] ?? role}
              size="small"
              sx={{
                borderRadius: 0,
                fontSize: 12,
                height: 22,
                backgroundColor: role === 'admin' ? colors.yellow : colors.lightGray,
                fontWeight: role === 'admin' ? 700 : 500,
              }}
            />
          ))}
        </Box>
      ),
    },
    {
      field: 'active',
      headerName: t('admin.roles.columns.active'),
      width: 80,
      sortable: true,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Switch
            size="small"
            checked={params.row.active}
            disabled={!isAdmin}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              e.stopPropagation();
              writeActive(params.row.id, e.target.checked);
            }}
            data-testid={`active-toggle-${params.row.id}`}
          />
        </Box>
      ),
    },
  ];

  // Comptes déjà là : sert au panneau d'invitation à refuser un doublon avant
  // de poser une invitation que personne ne consommera jamais.
  const knownEmails = useMemo(
    () => new Set(rows.map((r) => (r.data.email ?? '').toLowerCase()).filter(Boolean)),
    [rows],
  );

  return (
    <Box>
      {isAdmin && (
        <UserInvitesPanel departmentOptions={departmentOptions} knownEmails={knownEmails} />
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: 2,
          gap: { xs: 1, sm: 2 },
          flexWrap: 'wrap',
        }}
      >
        <TextField
          placeholder={t('common.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: { xs: '100%', sm: 280 }, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
          slotProps={{ htmlInput: { 'aria-label': t('admin.roles.searchAria') } }}
        />
        <TextField
          select
          size="small"
          label={t('admin.roles.filterGroup')}
          value={groupFilter}
          onChange={(e) => setGroupFilter(e.target.value)}
          sx={{ minWidth: { xs: '100%', sm: 200 } }}
          data-testid="users-group-filter"
        >
          <MenuItem value="all">{t('admin.roles.allGroups')}</MenuItem>
          {departmentOptions.map((d) => (
            <MenuItem key={d.id} value={d.id}>
              {d.name}
            </MenuItem>
          ))}
        </TextField>
        <Typography variant="caption" color="text.secondary">
          {t('admin.roles.count', { count: filtered.length })}
        </Typography>
      </Box>

      {writeError && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 0 }}
          onClose={() => setWriteError(null)}
        >
          {writeError}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }}>
          {error.message}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2, borderRadius: 0 }}>
        {t('admin.roles.managedInEntra')}
      </Alert>

      <Box sx={{ height: 600 }}>
        <DataGrid
          rows={gridRows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          disableColumnSelector
          density="standard"
          rowHeight={56}
          sx={{ ...adminDataGridSx, '& .row-inactive': { opacity: 0.55 } }}
          getRowClassName={(params) => (params.row.active ? '' : 'row-inactive')}
          initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
          pageSizeOptions={[25, 50, 100]}
        />
      </Box>
    </Box>
  );
}
